"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Code as Code2, Globe as Globe2, Sparkles, CalendarCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const services = [{
  title: "Technical SEO",
  to: "/TechnicalSeoPage",
  badge: "Core Infrastructure",
  description: "Eliminate crawl barriers, resolve indexing errors, and tune page architecture so search engines evaluate your site cleanly.",
  icon: Code2,
  deliverables: "Architecture audits, speed optimisation, structured data"
}, {
  title: "National SEO",
  to: "/NationalSeoPage",
  badge: "Market Visibility",
  description: "Compete for high-intent search queries across the UK with focused topical authority, strategic content, and clean link equity.",
  icon: Globe2,
  deliverables: "Keyword mapping, competitive positioning, content strategy"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservicescta" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3.5 py-1 text-xs font-medium text-secondary-foreground">
            <Sparkles className="size-3.5 text-accent" />
            <span>Consultancy services</span>
          </div>

          <h2 className="mt-4 font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Explore related search solutions
          </h2>
          <p className="mt-3 font-default text-base text-muted-foreground sm:text-lg">
            Work directly with me to resolve complex technical crawl barriers, refine site architecture, or expand high-intent national search visibility.
          </p>
        </div>

        {/* Related Services Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {services.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="group flex h-full flex-col justify-between border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md sm:p-8">
                  <div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex size-12 items-center justify-center rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-6 text-primary" />
                      </div>
                      <Badge variant="outline" className="border-border/70 text-xs font-normal">
                        {service.badge}
                      </Badge>
                    </div>

                    <CardHeader className="p-0 pt-6">
                      <CardTitle className="font-default text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="p-0 pt-6">
                      <p className="text-xs font-medium text-foreground/70">
                        Focus: <span className="text-muted-foreground">{service.deliverables}</span>
                      </p>
                    </CardContent>
                  </div>

                  <div className="pt-8">
                    <Button asChild variant="outline" className="w-full justify-between border-border text-foreground hover:bg-secondary hover:text-secondary-foreground">
                      <Link to={service.to}>
                        <span>Explore {service.title}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Final Consultation Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 rounded-2xl border border-border bg-card p-8 text-center shadow-sm sm:p-10 lg:p-12">
          <div className="mx-auto max-w-2xl">
            <h3 className="font-default text-2xl font-semibold text-foreground sm:text-3xl">
              Ready to clarify your search growth?
            </h3>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              Partner directly with me on technical architecture reviews, crawl diagnostics, and integrated search strategies tailored to your tech stack.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto">
                <Link to="/ContactPage">
                  <CalendarCheck2 className="size-4" />
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              Direct technical oversight &bull; Architecture & crawl audits &bull; Scalable search roadmaps
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}